import type React from 'react';
import { useEffect, useState, useRef } from 'react';

import { CircularProgress, InputAdornment, Typography } from '@mui/material';
import { OrdFormList, OrdIconStandard, OrdReferenceInput } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { dataSchema, SipIdentityExtensionUpdateFields } from '@ragu-components/ord-types';
import { debounce } from 'lodash';
import { TextInput, useTranslate, useSourceContext, useInput, SelectArrayInput } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import { SipIdentitiesPassword } from './SipIdentitiesPassword';

import type { FormType } from '@ragu-components/ord-components';

export interface SipIdentityInputsProps {
  type: FormType;
  sourcePrefix?: string;
}

const ServerSideError = ({ source }: { source: string }): React.ReactElement | null => {
  const { fieldState } = useInput({ source });
  if (fieldState.error === undefined) return null;
  return (
    <Typography color='error' variant='caption' sx={{ mt: 0.5, ml: 1.75 }}>
      {fieldState.error.message}
    </Typography>
  );
};

export const SipIdentityInputs = ({ type, sourcePrefix = '' }: SipIdentityInputsProps): React.ReactElement => {
  const { setError, clearErrors, getValues, setValue } = useFormContext();
  const { getSource } = useSourceContext();
  const translate = useTranslate();
  const dataProvider = useIngordDataProvider();

  const usernameSource = sourcePrefix
    ? `${sourcePrefix}.${SipIdentityExtensionUpdateFields.username}`
    : SipIdentityExtensionUpdateFields.username;
  const passwordSource = sourcePrefix
    ? `${sourcePrefix}.${SipIdentityExtensionUpdateFields.password}`
    : SipIdentityExtensionUpdateFields.password;
  const sipProfileSource = sourcePrefix
    ? `${sourcePrefix}.${SipIdentityExtensionUpdateFields.assigned_sip_profile}`
    : SipIdentityExtensionUpdateFields.assigned_sip_profile;

  const defaultUsername = getValues(getSource(usernameSource));
  const [loading, setLoading] = useState(false);
  const validateUsernameRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);

  useEffect(() => {
    const debounced = debounce((value: string, composedSource: string) => {
      void dataProvider
        .getList('sip_identities', {
          filter: { active: 'fiql', fiql: `username==${value}` },
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
        })
        .then((response) => {
          if (response.data.length > 0) {
            setValue(composedSource, value, { shouldTouch: true, shouldDirty: false });
            setError(
              composedSource,
              { type: 'manual', message: translate('resources.sip_identities.errors.usernameAlreadyExists') },
              { shouldFocus: false }
            );
          } else {
            clearErrors(composedSource);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }, 1000);

    validateUsernameRef.current = debounced;

    return () => {
      debounced.cancel();
    };
  }, [dataProvider, setError, clearErrors, translate, setLoading, setValue]);

  return (
    <>
      <OrdFormList icon={<OrdIconStandard name='credentials' />}>
        <TextInput
          source={usernameSource}
          isRequired
          resettable={type === 'create'}
          autoComplete='off'
          helperText='resources.sip_identities.helperTexts.username'
          onKeyPress={(event) => {
            if (loading && event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          onChange={(event) => {
            const value = event.target.value as string;
            const composed = getSource(usernameSource);
            if (value === '' || value === defaultUsername) {
              clearErrors(composed);
              setLoading(false);
              return;
            }
            setLoading(true);
            validateUsernameRef.current?.(value, composed);
          }}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position='start'>
                <CircularProgress size={20} />
              </InputAdornment>
            ) : undefined,
          }}
        />
      </OrdFormList>
      <OrdFormList sx={{ mt: -1 }}>
        <SipIdentitiesPassword
          source={passwordSource}
          isRequired
          helperText='resources.sip_identities.helperTexts.password'
        />
      </OrdFormList>
      <OrdFormList icon={<OrdIconStandard name='sip' />}>
        <SelectArrayInput
          variant='outlined'
          source={SipIdentityExtensionUpdateFields.device_types}
          choices={dataSchema.SipIdentityExtensionUpdate.properties.device_types.items.enum.map((item) => ({
            id: item,
            name: translate(`resources.sip_identities.fields.enum_device_types.${item}`),
          }))}
        />
        <OrdReferenceInput
          resource={Resources.assigned_sip_profiles}
          source={sipProfileSource}
          format={(item) => {
            if (item === null || item === undefined) return null;
            if (typeof item === 'object' && item !== null && 'id' in item) {
              return item.id;
            }
            if (typeof item === 'object') {
              return null;
            }
            return item;
          }}
        />
        <ServerSideError source={sipProfileSource} />
      </OrdFormList>
    </>
  );
};
